import { Button, Grid, Skeleton } from "@mui/material";
import CartProducts from "components/v1/common/CartProducts/CartProducts";
import { PickupAddress } from "components/v1/Orders/Address";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  updateReturnOrder,
  updateSelectedOrder,
} from "features/orders/orderSlice";
import {
  addPreviews,
  addProceedBool,
  addProductType,
  addSelectedReason,
  addSelectedSubReason,
  addUploads,
  addUploadMediaIds,
  setImageUploads,
} from "features/product/productSlice";
import {
  addRefundJSON,
  addRefundSource,
  addReturnedProducts,
  getPaymentDetails,
  getPaymentStatus,
} from "features/returnedProducts/returnedProductSlice";
import { getEmail, getPhone } from "features/user/userSlice";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getEncipherencode } from "utils/encipherencode";
import styled from "styled-components";
import Header from "../common/Header";
import { StyledExchange } from "../ReturnCart/styled";
import { ReactComponent as CheckCircle } from "assets/check-circle.svg";
import { selectedReturnOrderAPI } from "utils/api";
import { useTranslation } from "react-i18next";
import { clearReturnCartContext } from "utils/returnCartSession";
import { hasValidTrackingReference, versionedPath } from "utils/routes";

function Success() {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const userPhone = useSelector(getPhone);
  const userEmail = useSelector(getEmail);
  const rmsConfig = useSelector(getRmsConfig);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const requestIdParam = params.get("request_id");
  const referenceIdParam = params.get("reference_id");

  const [finishStatus, setfinishStatus] = useState(false);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!finishStatus) {
      setfinishStatus(true);
      let url = versionedPath("/orders");
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
    }
  };

  const merchantConfig = useSelector(getMerchantConfig);
  const paymentDetails = useSelector(getPaymentDetails);
  const paymentStatus = useSelector(getPaymentStatus);
  const [successProducts, setSuccessProducts] = useState(null);
  const [updatedAddress, setUpdatedAddress] = useState({});
  const [successLoader, setSuccessLoader] = useState(true);

  useEffect(() => {
    const requestId = [
      paymentStatus?.return_request_id,
      paymentStatus?.request_id,
      requestIdParam,
      paymentDetails?.return_request_id,
      paymentDetails?.request_id,
    ].find(hasValidTrackingReference);
    const referenceId = [
      paymentStatus?.reference_id,
      referenceIdParam,
      paymentDetails?.reference_id,
    ].find(hasValidTrackingReference);
    const createExchangeOrder =
      paymentStatus?.create_exchange_order ||
      paymentDetails?.create_exchange_order ||
      false;

    if (
      merchantConfig &&
      merchantConfig.merchant_id !== null &&
      (requestId || referenceId)
    ) {
      selectedReturnOrderAPI(
        merchantConfig.merchant_id,
        requestId,
        requestId ? null : referenceId,
        createExchangeOrder
      )
        .then((resp) => {
          setSuccessProducts(resp.data);
          setUpdatedAddress({
            addressLine1: resp.data.customer_address_line_1,
            addressLine2: resp.data.customer_address_line_2,
            city: resp.data.customer_address_city,
            pincode: resp.data.customer_address_pin,
            state: resp.data.customer_address_state,
          });
          setSuccessLoader(false);
        })
        .catch(() => {
          setSuccessLoader(false);
        });
      dispatch(updateSelectedOrder({}));
      dispatch(addSelectedReason(null));
      dispatch(addSelectedSubReason(null));
      dispatch(addUploads([]));
      dispatch(setImageUploads([]));
      dispatch(addUploadMediaIds([]));
      dispatch(addPreviews([]));
      dispatch(addReturnedProducts([]));
      dispatch(addProductType(null));
      dispatch(addProceedBool(false));
      dispatch(addRefundJSON({}));
      dispatch(addRefundSource(null));
      clearReturnCartContext();
    } else if (userEmail == "" && userPhone == "") {
      let url = "/";
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
    }
    window.history.pushState(
      null,
      null,
      encipherencode
        ? `${window.location.pathname}?encipherencode=${encipherencode}`
        : window.location.pathname
    );
    window.addEventListener("popstate", onBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, []);

  const StyledSuccess = styled.div`
    padding: 100px 20px;
    background: #fff;
    text-align: center;

    .title {
      font-size: 1.5rem;
      margin: 16px;
      font-weight: 700;
      color: #1c1b1b;
    }

    .return-id {
      font-size: 1.25rem;
      margin: 16px;
      color: #3B444D;
    }

    .info {
      font-size: 1rem;
      margin: 16px;
      color: #3B444D;
    }

    @media (max-width: 800px) {
      .title {
        font-size: 16px;
        margin: 0px;
      }
      .return-id {
        font-size: 14px;
      }
      .info {
        font-size: 12px;
      }
    }
  `;

  const handleTracking = () => {
    dispatch(updateReturnOrder(successProducts.id));
    let url = versionedPath("/tracking");
    if (encipherencode) {
      url += `?encipherencode=${encipherencode}`;
    }
    navigate(url);
  };

  return (
    <>
      <Header />
      {successProducts !== null && !successLoader ? (
        <StyledSuccess>
          <p className="title">
            <CheckCircle style={{ marginRight: "5px" }} />
            {t("success.your")}
            {rmsConfig.only_replacement
              ? ` ${t("success.replacement")}`
              : successProducts.return_type == "refund"
              ? ` ${t("success.return")} `
              : successProducts.return_type == "exchange"
              ? ` ${t("success.exchange")} `
              : ` ${t("success.returnAndExchange")} `}{" "}
            {t("success.requestPlacedSuccessfully")}
          </p>
          <p className="return-id">
            {t("success.trackRequest")}{" "}
            <span
              style={{
                textDecoration: "underline",
                fontWeight: "500",
                cursor: "pointer",
              }}
              onClick={() => handleTracking()}
            >
              {" "}
              #{successProducts.reference_id}
            </span>
          </p>

          <StyledExchange
            location={location.pathname}
            primaryColor={merchantConfig.primary_theme_color}
            style={{
              textAlign: "left",
              marginBottom: "40px",
              justifyContent: "center",
              border: "none",
              width: "100%",
            }}
          >
            <div
              className="products-return"
              style={{
                display: "flex",
                maxWidth: "600px",
                justifyContent: "center",
              }}
            >
              <Grid
                alignItems="center"
                container
                gridTemplateColumns="repeat(12, 1fr)"
                className="cart-products"
              >
                {successProducts.line_items !== undefined &&
                  successProducts.line_items.length > 0 &&
                  successProducts.line_items.map((product, i) => {
                    return (
                      <Grid
                        key={i}
                        justifyContent="center"
                        item
                        xs={12}
                        style={{
                          marginBottom: "1rem",
                        }}
                      >
                        <CartProducts
                          cart_type="pending"
                          product={product}
                          location={location.pathname}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </div>
          </StyledExchange>

          <div className="box address">
            <PickupAddress
              edit={false}
              updatedAddress={updatedAddress}
              serviceable={true}
            />
          </div>
          <div>
            <Button
              color="primary"
              type="link"
              target="_blank"
              href={`https://${merchantConfig.domain}`}
              style={{
                display: "block",
                margin: "20px auto",
                width: "fit-content",
              }}
              variant="contained"
            >
              Explore Our special offers
            </Button>
            <Button
              color="primary"
              type="link"
              href={
                encipherencode
                  ? `${versionedPath("/orders")}?encipherencode=${encipherencode}`
                  : versionedPath("/orders")
              }
              style={{ marginBottom: "20px" }}
              variant="text"
            >
              Return other products
            </Button>
          </div>
          {merchantConfig.whatsapp_help &&
            merchantConfig.whatsapp_help !== "" && (
              <a
                href={merchantConfig.whatsapp_help}
                target="_blank"
                rel="noreferrer"
                style={{ color: "initial", fontSize: "12px" }}
              >
                Contact Support
              </a>
            )}
        </StyledSuccess>
      ) : (
        <div style={{ padding: "100px" }}>
          <Skeleton style={{ width: "100%" }} height={50} />
          <div style={{ display: "flex" }}>
            <Skeleton height={100} style={{ width: "47.5%" }} />
            <Skeleton
              height={100}
              style={{ marginLeft: "100px", width: "47.5%" }}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Success;
